import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Code2,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Zap,
  Target,
  Lightbulb,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

// Floating language text configuration
const floatingLanguages = [
  {
    name: 'C++',
    color: 'text-blue-400/50',
    size: 'text-4xl md:text-5xl',
    position: 'top-[10%] left-[6%]',
    animation: { y: [0, -20, 0], rotate: [0, 5, -5, 0] },
    duration: 5,
    delay: 0,
  },
  {
    name: 'Python',
    color: 'text-yellow-400/50',
    size: 'text-3xl md:text-4xl',
    position: 'top-[15%] right-[10%]',
    animation: { y: [0, 15, 0], rotate: [0, -8, 8, 0] },
    duration: 6,
    delay: 0.5,
  },
  {
    name: 'JS',
    color: 'text-yellow-500/50',
    size: 'text-4xl md:text-5xl',
    position: 'bottom-[18%] left-[8%]',
    animation: { y: [0, -18, 0], scale: [1, 1.1, 1] },
    duration: 5.5,
    delay: 1,
  },
  {
    name: 'TS',
    color: 'text-blue-500/50',
    size: 'text-3xl md:text-4xl',
    position: 'bottom-[25%] right-[6%]',
    animation: { y: [0, 14, 0], x: [0, -10, 0] },
    duration: 6.5,
    delay: 1.5,
  },
  {
    name: 'Go',
    color: 'text-cyan-400/40',
    size: 'text-3xl md:text-4xl',
    position: 'top-[40%] left-[3%]',
    animation: { y: [0, -12, 0], rotate: [0, 10, -10, 0] },
    duration: 5,
    delay: 0.8,
  },
  {
    name: 'Rust',
    color: 'text-orange-500/40',
    size: 'text-2xl md:text-3xl',
    position: 'top-[30%] right-[5%]',
    animation: { y: [0, 12, 0], x: [0, 8, 0] },
    duration: 7,
    delay: 2,
  },
  {
    name: 'Java',
    color: 'text-red-400/40',
    size: 'text-2xl md:text-3xl',
    position: 'bottom-[35%] left-[4%]',
    animation: { y: [0, -10, 0], scale: [1, 1.05, 1] },
    duration: 6,
    delay: 1.2,
  },
  {
    name: 'SQL',
    color: 'text-green-400/40',
    size: 'text-2xl md:text-3xl',
    position: 'top-[22%] left-[12%]',
    animation: { y: [0, 8, 0], rotate: [0, -6, 6, 0] },
    duration: 5.5,
    delay: 0.3,
  },
]

const promptExamples = [
  {
    id: 1,
    title: 'Code Generation',
    bad: {
      prompt: 'Write sorting code',
      issues: ['No language specified', 'No algorithm preference', 'No constraints'],
      result: [
        'Random language choice',
        'No error handling',
        'Missing documentation',
        'May not fit your needs',
      ],
    },
    good: {
      prompt: 'Write a Python implementation of merge sort optimized for memory efficiency, including time complexity analysis and error handling for empty arrays.',
      benefits: ['Specific language', 'Clear algorithm', 'Defined constraints', 'Expected behavior'],
      result: [
        'Clean Python implementation',
        'O(n log n) complexity noted',
        'Empty array handling included',
        'Memory optimization explained',
      ],
    },
  },
  {
    id: 2,
    title: 'Debugging Help',
    bad: {
      prompt: 'My code doesn\'t work, fix it',
      issues: ['No error message', 'No code context', 'No expected behavior'],
      result: [
        'Generic suggestions',
        'Multiple unrelated fixes',
        'May introduce new bugs',
        'Wastes your time',
      ],
    },
    good: {
      prompt: 'This React useEffect causes infinite re-renders. Expected: fetch data once on mount. Actual: continuous API calls. Here\'s the code: [code]. What\'s causing this?',
      benefits: ['Clear problem', 'Expected vs actual', 'Context provided', 'Specific question'],
      result: [
        'Identifies missing dependency array',
        'Explains why loop occurs',
        'Provides correct useEffect pattern',
        'Warns about common pitfalls',
      ],
    },
  },
  {
    id: 3,
    title: 'Code Review',
    bad: {
      prompt: 'Review my code',
      issues: ['No focus area', 'No quality criteria', 'No context'],
      result: [
        'Surface-level comments',
        'Style nitpicks only',
        'Misses critical issues',
        'No actionable insights',
      ],
    },
    good: {
      prompt: 'Act as a senior security engineer. Review this authentication code for OWASP Top 10 vulnerabilities. Focus on SQL injection, XSS, and session management. [code]',
      benefits: ['Role assignment', 'Specific focus', 'Clear criteria', 'Security context'],
      result: [
        'SQL injection vectors identified',
        'XSS escape recommendations',
        'Session fixation warnings',
        'Remediation code provided',
      ],
    },
  },
]

const impactStats = [
  { value: '68%', label: 'Less back-and-forth', source: 'Microsoft Research' },
  { value: '434%', label: 'Job posting increase', source: 'LinkedIn 2025' },
  { value: '85%', label: 'AI product heavy lifting', source: 'Industry Report' },
]

export default function PromptingLanguageSlide({ isActive: _isActive }: SlideProps) {
  const [selectedExample, setSelectedExample] = useState(0)
  const [showGood, setShowGood] = useState(true)

  return (
    <Slide>
      {/* Floating language text - TitleSlide style */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {floatingLanguages.map((lang) => (
          <motion.div
            key={lang.name}
            animate={lang.animation}
            transition={{
              duration: lang.duration,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: lang.delay,
            }}
            className={`absolute ${lang.position}`}
          >
            <span className={`font-mono font-bold ${lang.size} ${lang.color}`}>
              {lang.name}
            </span>
          </motion.div>
        ))}
      </div>

      <StaggerContainer className="h-full flex flex-col relative z-10">
        {/* Header */}
        <StaggerItem>
          <div className="mb-10 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
              Prompting: <span className="text-gradient">A New Language</span>
            </h2>
            <p className="text-base text-white/60">
              Effective prompts = Effective results
            </p>
          </div>
        </StaggerItem>

        {/* Main content: Good vs Bad Prompts */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Example selector */}
            <div className="lg:col-span-1 flex lg:flex-col gap-2">
              {promptExamples.map((example, index) => (
                <motion.button
                  key={example.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  onClick={() => setSelectedExample(index)}
                  className={`flex-1 lg:flex-none glass rounded-xl p-3 text-left transition-all ${
                    selectedExample === index
                      ? 'ring-2 ring-electric-cyan bg-electric-cyan/10'
                      : 'hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedExample === index ? 'bg-electric-cyan/20' : 'bg-white/10'
                    }`}>
                      {index === 0 ? <Code2 className="w-5 h-5 text-electric-cyan" /> :
                       index === 1 ? <Zap className="w-5 h-5 text-electric-orange" /> :
                       <Target className="w-5 h-5 text-electric-purple" />}
                    </div>
                    <div>
                      <p className={`font-semibold ${selectedExample === index ? 'text-electric-cyan' : 'text-white'}`}>
                        {example.title}
                      </p>
                      <p className="text-xs text-white/40">Click to view</p>
                    </div>
                  </div>
                </motion.button>
              ))}

              {/* Toggle button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="glass rounded-xl p-3"
              >
                <p className="text-xs text-white/40 mb-2">Compare:</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowGood(false)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                      !showGood ? 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30' : 'bg-white/5 text-white/50'
                    }`}
                  >
                    <XCircle className="w-4 h-4" />
                    Bad
                  </button>
                  <button
                    onClick={() => setShowGood(true)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                      showGood ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30' : 'bg-white/5 text-white/50'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Good
                  </button>
                </div>
              </motion.div>
            </div>

            {/* Prompt comparison display */}
            <div className="lg:col-span-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${selectedExample}-${showGood}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="glass rounded-2xl p-5 h-full flex flex-col"
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4">
                    {showGood ? (
                      <CheckCircle2 className="w-6 h-6 text-green-400" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-400" />
                    )}
                    <h3 className={`text-xl font-bold ${showGood ? 'text-green-400' : 'text-red-400'}`}>
                      {showGood ? 'Effective Prompt' : 'Ineffective Prompt'}
                    </h3>
                    <span className="ml-auto text-sm text-white/40">
                      {promptExamples[selectedExample].title}
                    </span>
                  </div>

                  {/* Prompt text */}
                  <div className={`rounded-xl p-5 mb-4 font-mono text-lg md:text-xl ${
                    showGood ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
                  }`}>
                    <div className="flex items-start gap-3">
                      <MessageSquare className={`w-6 h-6 mt-1 flex-shrink-0 ${showGood ? 'text-green-400' : 'text-red-400'}`} />
                      <p className="text-white/90 leading-relaxed">
                        "{showGood ? promptExamples[selectedExample].good.prompt : promptExamples[selectedExample].bad.prompt}"
                      </p>
                    </div>
                  </div>

                  {/* Analysis */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Issues / Benefits */}
                    <div className={`rounded-xl p-4 ${showGood ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
                      <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${showGood ? 'text-green-400' : 'text-red-400'}`}>
                        {showGood ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {showGood ? 'What Makes It Work' : 'Problems'}
                      </h4>
                      <ul className="space-y-1.5">
                        {(showGood ? promptExamples[selectedExample].good.benefits : promptExamples[selectedExample].bad.issues).map((item, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 + i * 0.05 }}
                            className="text-sm text-white/70 flex items-center gap-2"
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${showGood ? 'bg-green-400' : 'bg-red-400'}`} />
                            {item}
                          </motion.li>
                        ))}
                      </ul>
                    </div>

                    {/* Expected Result */}
                    <div className="glass rounded-xl p-4">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-electric-cyan">
                        <Lightbulb className="w-4 h-4" />
                        Expected AI Response
                      </h4>
                      <ul className="space-y-1.5">
                        {(showGood ? promptExamples[selectedExample].good.result : promptExamples[selectedExample].bad.result).map((item, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15 + i * 0.05 }}
                            className={`text-sm flex items-center gap-2 ${showGood ? 'text-green-400' : 'text-red-400'}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${showGood ? 'bg-electric-cyan' : 'bg-red-400'}`} />
                            {item}
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </StaggerItem>

        {/* Impact stats */}
        <StaggerItem>
          <div className="mt-4 mb-[100px]">
            <div className="flex justify-center gap-6 md:gap-12">
              {impactStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-gradient">{stat.value}</div>
                  <div className="text-xs text-white/50">{stat.label}</div>
                  <div className="text-[10px] text-white/30">{stat.source}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
